import React, { useMemo } from 'react'
import { Info } from 'lucide-react'

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/features/shadcn/ui/tooltip'
import { DashboardWidget } from 'modules/widgetSpec'
import { describeWidget } from './describeWidget'

/**
 * ⓘ help for a custom widget — the counterpart to MetricInfoTooltip on built-in
 * KPI tiles. Explains what the number is and where it came from, derived from the
 * spec itself so it can never drift from what actually executes.
 *
 * Carries its own TooltipProvider: widget cards render both inside the KPI row
 * (which has a hoisted provider) and in the Custom Widgets grid / chat preview
 * (which don't). Nesting a provider is harmless; missing one throws.
 */
const WidgetInfoTooltip = ({ widget }: { widget: DashboardWidget }) => {
  const explanation = useMemo(() => {
    try {
      return describeWidget(widget)
    } catch (e) {
      return null
    }
  }, [widget])

  if (!explanation) return null

  return (
    <TooltipProvider delayDuration={250} skipDelayDuration={150} disableHoverableContent>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            tabIndex={0}
            aria-label={`About ${widget.title}`}
            className="metric-info-trigger tw-shrink-0"
          >
            <Info className="tw-size-3.5 tw-text-slate-400" strokeWidth={2} aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          sideOffset={6}
          collisionPadding={8}
          className="tw-z-[1100] tw-max-w-xs tw-whitespace-normal tw-text-left tw-font-normal tw-leading-snug"
        >
          <div className="tw-font-medium">{explanation.summary}</div>
          {explanation.details.length ? (
            <ul className="tw-mb-0 tw-mt-1.5 tw-list-disc tw-pl-4 tw-text-xs tw-text-slate-300">
              {explanation.details.map((detail, index) => (
                <li key={`${index}-${detail}`}>{detail}</li>
              ))}
            </ul>
          ) : null}
          {explanation.prompt ? (
            <div className="tw-mt-1.5 tw-border-t tw-border-slate-700 tw-pt-1.5 tw-text-xs tw-italic tw-text-slate-400">
              You asked: “{explanation.prompt}”
            </div>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default React.memo(WidgetInfoTooltip)
